<template>
    <b-modal id="assign-in-bulk-modal" :modalClass="`assign-in-bulk-modal`" :visible="isOpen" @hide="onClose"
        centered>
        <div slot="modal-header" class="d-flex justify-content-between w-100">
            <h2>{{ resource['assignInBulkModalMainTitle'] }}</h2>
            <i class="icon-close custom-modal-close cursor-pointer" @click="onClose" />
        </div>
        <div class="first-screen" v-if="showSelectionScreen">
            <div class="bulk-modal-body">
                <img :src="require('@/assets/images/bulk-modal.png')" loading="lazy" alt="assign-bulk">
                <p class="modal-title mb-1 mt-4">{{ resource['assignInBulkModalDescTitle'] }}</p>
                <p class="modal-description">{{ resource['assignInBulkModalDesc'] }}</p>
            </div>
        </div>

        <div class="second-screen" v-else>
            <div class="bulk-modal-body">
                <p class="modal-description">{{ resource['assignInBulkModalSecondScreenDesc'] }}
                </p>
                <div class="candidate-group" v-for="group in candidateGroups" :key="group.id">
                    <div class="candidate-group-items" :class="{ 'groups': candidateGroups.length > 1 }">
                        <p class="candidate-group-title">{{ group.name }}</p>
                        <div class="candidate-group-icon" @click="candidateGroups.length > 1 ? removeCandidateGroup(group.id) : {}">
                            <i class="icon-delete-two"></i>
                        </div>
                    </div>
                    <RangeSlider
                      :key="'range' + group.id"
                      :id="`range-${group.id}`"
                      v-model="group.candidateCount"
                      :min="0"
                      :max="totalCandidateCount"
                      :restOfCandidate="restOfCandidate"
                      :step="1"
                    />
                    <Assignees
                      :key="`assignees-${group.id}`"
                      :id="`assignees-${group.id}`"
                      :assigned-evaluators="group.assignedEvaluators"
                      :showTextButton="true"
                      @addUser="addUserToGroup(group.id, $event)"
                      @removeUser="removeUserFromGroup(group.id, $event)"
                      :canAssignEvaluator="true"
                    />
                </div>
                <p class="error-text" v-if="groupErrorText">{{ groupErrorText }}</p>
                <b-button variant="add" class="bulk-button-new-group" @click="addCandidateGroup">{{ resource['assignInBulkModalAddNewGroup'] }}
                    <i class="icon-add"></i>
                </b-button>
            </div>
            <div class="blue-info" v-if="restOfCandidate > 0">
                <i class="icon-info"></i>
                 <span v-html="resource['assignInBulkModalBlueInfoText'].replace('{restOfCandidate}', totalCandidateCount - restOfCandidate).replace('{totalCandidateCount}', totalCandidateCount)"></span>
            </div>
            <div class="green-info" v-if="restOfCandidate === 0 && totalCandidateCount > 0">
                <div class="green-info-icon"><i class="icon-tick-border"></i></div>
                <span v-html="resource['assignInBulkModalBlueInfoText'].replace('{restOfCandidate}', totalCandidateCount).replace('{totalCandidateCount}', totalCandidateCount)"></span>
            </div>
        </div>
        <div slot="modal-footer" class="bulk-modal-footer">
            <template v-if="!showSelectionScreen">
                <b-button variant="cancel" class="bulk-button-cancel" :disabled="candidateGroups.length <= 1 || hasDistributed" :class="{ 'disabled': candidateGroups.length <= 1 }" @click="distributeEqually">{{ resource['assignInBulkModalDistributeBtnText'] }}</b-button>
                <b-button variant="continue" class="bulk-button-continue" @click="checkAndAssignCandidates()">{{ resource['assignInBulkModalAssigneeBtnText'] }}</b-button>
            </template>
            <template v-else>
                <b-button variant="cancel" class="bulk-button-cancel" @click="onClose">{{ resource['assignInBulkModalCancelBtnText'] }}</b-button>
                <b-button variant="continue" class="bulk-button-continue" @click="continueHandler"> {{ resource['assignInBulkModalContinueBtnText'] }}</b-button>
            </template>
        </div>
    </b-modal>
</template>

<script>
import HrVenueService from '@/services/hrvenue.service';
import RangeSlider from '@/components/common/RangeSlider.vue';
import Assignees from '../common/Assignees.vue';
import { showNotification } from '../../static/helpers';

export default {
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    totalCandidateCount: {
      type: Number,
      required: true,
      default: 0,
    },
    interviewId: {
      type: String,
      required: true,
    },
    selectedStatus: {
      type: Number,
      default: 0,
    },
    searchText: {
      type: String,
      default: '',
    },
    showMine: {
      type: Boolean,
      default: true,
    },
    selectedAssigness: {
      type: Array,
      default: () => [],
    },
    showUnassigned: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    RangeSlider,
    Assignees,
  },
  data() {
    return {
      showSelectionScreen: true,
      candidateGroups: [],
      groupErrorText: null,
      restOfCandidate: 0,
      hasDistributed: false,
    };
  },
  watch: {
    totalCandidateCount: {
      immediate: true,
      handler() {
        this.initializeCandidateGroups();
      },
    },
    'candidateGroups.candidateCount': {
      immediate: true,
      handler() {
        this.restOfCandidate = this.totalCandidateCount - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
      },
    },
    candidateGroups: {
      deep: true,
      handler() {
        this.restOfCandidate = this.totalCandidateCount
        - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);

        this.checkDistributionState();
        this.groupErrorText = null;
      },
    },
  },
  computed: {
    isAllCandidatesAssigned() {
      return this.totalCandidateCount > 0 && this.restOfCandidate === 0;
    },
  },
  methods: {
    initializeCandidateGroups() {
      this.candidateGroups = [
        {
          id: 1,
          name: 'Candidate Group 1',
          candidateCount: 0,
          assignedEvaluators: [],
        },
      ];
      this.restOfCandidate = this.totalCandidateCount - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
    },
    onClose() {
      this.$emit('close');
      this.showSelectionScreen = true;
    },
    continueHandler() {
      this.showSelectionScreen = false;
    },
    addCandidateGroup() {
      this.restOfCandidate = this.totalCandidateCount - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
      if (this.restOfCandidate <= 0) {
        this.groupErrorText = 'To add more users, reduce the number of people in other groups.';
        return;
      }

      const newGroupId = this.candidateGroups.length + 1;
      this.candidateGroups.push({
        id: newGroupId,
        name: `Candidate Group ${newGroupId}`,
        candidateCount: 0,
        assignedEvaluators: [],
      });
      this.restOfCandidate = this.totalCandidateCount
    - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
      this.hasDistributed = false;
    },

    removeCandidateGroup(groupId) {
      this.candidateGroups = this.candidateGroups.filter((group) => group.id !== groupId);
      this.hasDistributed = false;
    },

    addUserToGroup(groupId, user) {
      const group = this.candidateGroups.find((g) => g.id === groupId);
      if (group && user) {
        group.assignedEvaluators.push(user);
      }
    },
    removeUserFromGroup(groupId, user) {
      const group = this.candidateGroups.find((g) => g.id === groupId);
      if (group && user) {
        group.assignedEvaluators = group.assignedEvaluators.filter((c) => c.id !== user.id);
      }
    },

    getEvaluatorColor(evaluator) {
      return evaluator.color || this.colorPalette[0]; // Eğer renk yoksa varsayılan rengi kullan
    },

    removeCandidateFromGroup(groupId, candidateId) {
      const group = this.candidateGroups.find((g) => g.id === groupId);
      if (group) {
        group.assignedEvaluators = group.assignedEvaluators.filter((c) => c.candidateId !== candidateId);
      }
    },
    onSliderChange() {
      // Slider değiştiğinde hasDistributed false olsun
      this.hasDistributed = false;
      // restOfCandidate değerini güncelle
      this.restOfCandidate = this.totalCandidateCount - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
    },

    // Dağıtım durumunu kontrol eden metod
    checkDistributionState() {
      // Eğer manual değişiklik varsa hasDistributed'ı false yap
      // (Örneğin adil olmayan bir dağıtım varsa)
      if (this.candidateGroups.length > 1) {
        const firstGroupCount = this.candidateGroups[0].candidateCount;
        let isEquallyDistributed = true;

        // Tüm grupların yaklaşık aynı sayıya sahip olup olmadığını kontrol et
        for (let i = 1; i < this.candidateGroups.length; i += 1) {
          // En fazla 1 fark olabilir (kalan olduğunda)
          if (Math.abs(this.candidateGroups[i].candidateCount - firstGroupCount) > 1) {
            isEquallyDistributed = false;
            break;
          }
        }

        // Eğer eşit dağıtım yoksa ama distribute edilmiş görünüyorsa,
        // hasDistributed'ı false yap
        if (!isEquallyDistributed && this.hasDistributed) {
          this.hasDistributed = false;
        }
      }
    },
    distributeEqually() {
    // Eğer sadece bir grup varsa işlem yapma
      if (this.candidateGroups.length <= 1) return;

      // Her gruba eşit dağıtım yap
      const equalShare = Math.floor(this.totalCandidateCount / this.candidateGroups.length);
      let remainder = this.totalCandidateCount % this.candidateGroups.length;

      this.candidateGroups.forEach((group) => {
      // Temel eşit payı ata
        group.candidateCount = equalShare;

        // Kalan varsa, ilk gruplara birer tane ekstra ekle
        if (remainder > 0) {
          group.candidateCount += 1;
          remainder -= 1;
        }
      });
      // restOfCandidate değerini güncelle
      this.restOfCandidate = this.totalCandidateCount
      - this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);

      this.hasDistributed = true;
    },

    async checkAndAssignCandidates() {
      if (this.restOfCandidate > 0) {
        showNotification(
          this.resource['Label.Error'],
          this.resource.allCandidatesNotSelectedError,
          'error',
        );
        return;
      }
      const totalGroupCandidateCount = this.candidateGroups.reduce((acc, group) => acc + group.candidateCount, 0);
      if (totalGroupCandidateCount > this.totalCandidateCount) {
        showNotification(
          this.resource['Label.Error'],
          this.resource.selectedCandidatesCountGreaterThanTotalError,
          'error',
        );
        return;
      }
      const isAllAssingedEvaluators = this.candidateGroups.every((group) => group.assignedEvaluators.length > 0);
      if (!isAllAssingedEvaluators) {
        showNotification(
          this.resource['Label.Error'],
          this.resource.evaluatorsNotAssignedError,
          'error',
        );
        return;
      }
      try {
        //   const { totalInterviewCandidateCount } = await HrVenueService.getTotalCandidateCount(this.interviewId);
        //   if (this.totalCandidateCount !== totalInterviewCandidateCount) {
        //     return false;
        //   }
        const newCandidateGroup = this.candidateGroups.map((group) => ({
          candidateCount: group.candidateCount,
          assignmentUserIds: group.assignedEvaluators.map((evaluator) => evaluator.id),
        }));
        const candidateFilters = {
          status: this.selectedStatus,
          searchText: this.searchText,
          showMine: this.showMine,
          showUnassigned: this.showUnassigned,
          assignmentUserIds: this.selectedAssigness.filter((item) => item !== -1),
        };
        const response = await HrVenueService.bulkAssignEvaluatorToCandidate(this.interviewId, newCandidateGroup, candidateFilters);
        if (response === true) {
          showNotification(
            this.resource['label.Success'],
            this.resource.bulkAssigneSuccessMessage,
            'success',
          );
          dataLayer.push({
            event: 'ga4_event',
            event_name: 'assign_in_bulk',
            page_type: 'interview-detail',
            site_type: 'interview',
          });
          this.$emit('showAssignBulkInfo');
          this.onClose();
        }
      } catch (error) {
        //
      }
    },
  },

};

</script>
<style lang="scss">
.assign-in-bulk-modal {
    display: flex;
    justify-content: center;
    align-items: center;

    .modal-header{
        border-radius: 4px 4px 0px 0px;
        h2{
            color: #333;
            font-family: Roboto;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 22px;
        }
    }

    .modal-dialog {
        max-width: 520px !important;
    }

    .first-screen {
      width: 100%;

      .modal-description {
        width: 308px;
      }
    }

    .second-screen {
      height: 430px;
      overflow: hidden;
        .bulk-modal-body {
            padding: 0px 24px;
            align-items: flex-start;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            margin: 16px 0px 16px;
            height: 396px;

            .modal-description {
                color: #333;
                font-family: Roboto;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 20px;
                text-align: left;
                align-self: flex-start;
            }

            .candidate-group {
                display: flex;
                padding: var(--Padding-Large, 16px);
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                gap: 8px;
                align-self: stretch;
                border-radius: 8px;
                border: 1px solid #E5E5E5;
                background: #FFF;
                margin-top: 16px;

                &:has(.show) {
                  z-index: 9999;
                }

                .candidate-group-items {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    margin-bottom: 12px;

                    .candidate-group-icon{
                        border: 1px solid #C3C3C3;
                        border-radius: 50%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        cursor: pointer;
                        padding: 5px;
                    }

                    i {
                        font-size: 16px;
                        color: #C3C3C3;
                    }
                    &.groups {
                        i{
                            color: #333;
                        }
                    }
                }

                .candidate-group-title {
                    color: #353535;
                    font-family: Roboto;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 140%;
                }
            }

            .bulk-button-new-group {
                display: flex;
                padding: 6px 0px 6px 8px;
                gap: 8px;
                margin-top: 16px;
                justify-content: center;
                align-items: center;
                border-radius: 40px;
                border: 1px solid #ff8d06;
                background: #ff8d06;
                color: #fff;
                text-align: center;
                font-family: Roboto;
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: 18px;
                transition: all 0.3s ease-in-out;

                &:hover {
                  background: #FF7415 !important;
                }
            }
        }

        .blue-info {
            display: flex;
            padding: 8px;
            align-items: center;
            justify-content: center;
            gap: 8px;
            border: 1px solid #B7E1F3;
            background: #DBF0F9;
            color: #114E67;
            margin-bottom: 0px;
            transition: all 0.3s ease; // Renk değişimi için geçiş efekti
            position: absolute;
            width: 100%;
            bottom: 0px;

            i {
                font-size: 19px;
                align-items: center;
                justify-content: center;
                display: flex;
            }

            span {
                font-feature-settings: 'liga' off, 'clig' off;
                font-family: Roboto;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 18px;
            }
        }
        .green-info {
            display: flex;
            padding: 8px;
            align-items: center;
            justify-content: center;
            gap: 8px;
            border: 1px solid #B6EAC9;
            background: #DBF4E4;
            color: #19552E;
            margin-bottom: 0px;
            position: absolute;
            width: 100%;
            bottom: 0px;

            .green-info-icon{
                align-items: center;
                justify-content: center;
                display: flex;
            }

            i {
                font-size: 19px;
                color: #19552E;
            }

            span {
                font-feature-settings: 'liga' off, 'clig' off;
                font-family: Roboto;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 18px;
            }
        }

    }

    .bulk-modal-body {
        height: 100%;
        justify-content: center;
        display: flex;
        align-items: center;
        flex-direction: column;
        overflow: overlay;
        scrollbar-gutter: stable both-edges;
        &::-webkit-scrollbar {
            width: 7px !important;
        }
        &::-webkit-scrollbar-track {
            background-color: #fff !important;
            border-radius: 2px !important;
        }
        &::-webkit-scrollbar-thumb {
            border-radius: 2px !important;
            background: linear-gradient(to right, #D8D8D8 3px, #FFF 4px) !important;
        }
    }

    .modal-footer{
        padding: 0px;
        border-radius: 0px 0px 4px 4px;
    }
    .modal-body {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 0 !important;
        margin: 0 !important;
        text-align: center;
        height: 466px;

        &:has(.second-screen) {
          justify-content: flex-start;
        }

        .modal-title {
            font-family: Roboto;
            font-size: 20px;
            font-style: normal;
            font-weight: 700;
            line-height: 26px;
            color: #353535;
        }

        .modal-description {
            font-family: Roboto;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 20px;
            color: #858585;
        }
    }

    .bulk-modal-footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 4px;
        width: 100%;
        border-top: 1px solid #E4E4E4;
        padding: 16px 24px;
    }

    .bulk-button-cancel {
        display: flex;
        padding: 9px 16px;
        justify-content: center;
        align-items: center;
        border-radius: 114px;
        border: 1px solid #ff8d06;
        background: transparent !important;
        color: var(--main-colors-orange, #ff8d06);
        text-align: center;
        font-family: Roboto;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 18px;
        transition: all 0.3s ease-in-out;

        &:hover {
            background: #FF8D06 !important;
            color: #fff !important;

            path {
                fill: #fff !important;
            }
        }
        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            border-color: #ccc;
            color: #C3C3C3;

        &:hover {
            background: transparent !important;
            color: #C3C3C3 !important;
        }
    }
    }

    .bulk-button-continue {
        display: flex;
        padding: 9px 16px;
        justify-content: center;
        align-items: center;
        border-radius: 114px;
        border: 1px solid #ff8d06;
        background: #ff8d06;
        color: #fff;
        text-align: center;
        font-family: Roboto;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 18px;
        transition: all 0.3s ease-in-out;

        &:hover {
          background: #FF7415;
        }
    }

    .divider-bottom {
        width: 100%;
        height: 1px;
        background: #E4E4E4;
        margin-top: 16px;
        padding: 0 !important;
        box-sizing: border-box;
        width: 100%;
    }

    .error-text{
        padding-top: 4px;
        color: #DE4747;
        font-family: Roboto;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px;
    }
}
</style>
